import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils.js';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-card text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-accent text-white hover:bg-accent/90 active:bg-accent/80',
        outline:
          'border border-border bg-transparent text-text hover:border-border-strong hover:bg-border/30',
        secondary: 'bg-border/60 text-text hover:bg-border/80',
        ghost: 'text-text hover:bg-border/30',
        link: 'text-accent underline-offset-4 hover:underline',
        destructive: 'bg-accent text-white hover:bg-accent/90',
        // Submission Cockpit action language (shared across surfaces): crimson
        // accent = bind/primary only; success = presenting (filled when on);
        // info = log/6-mo (outline-info); dashed = alternate (dashed, info text).
        accent: 'bg-accent text-white hover:bg-accent/90 active:bg-accent/80',
        success: 'bg-success text-bg hover:bg-success/90',
        info: 'border border-info/60 bg-transparent text-info hover:bg-info-soft',
        dashed: 'border border-dashed border-border-strong bg-transparent text-info hover:bg-info-soft',
      },
      size: {
        default: 'min-h-[44px] px-4 py-2',
        sm: 'min-h-[36px] px-3 text-xs',
        lg: 'min-h-[48px] px-6',
        icon: 'h-11 w-11',
        xs: 'h-7 px-2 text-[11px] gap-1 rounded-md',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

const Button = forwardRef(function Button(
  { className, variant, size, asChild = false, ...props },
  ref
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  );
});

export { Button, buttonVariants };
